import React from 'react';
import { useTheme } from '@/contexts/ThemeContext';
import { Link, useLocation } from 'react-router-dom';
import { useAllFeatureAccess } from '../../hooks/useFeatureAccess';
import {
  LayoutDashboard,
  FolderOpen,
  FileText,
  Languages,
  PenTool,
  BookOpen,
  Database,
  ClipboardCheck,
  Settings,
  HelpCircle,
  Sparkles,
  Activity,
  Target,
  X,
  FileSearch,
  Gift,
  Lock,
  CreditCard,
  BarChart2,
  Users,
  Briefcase
} from 'lucide-react';

const SidebarApple = ({ collapsed, onToggle, isMobileOpen, onCloseMobile }) => {
  const { isDarkMode } = useTheme();
  const location = useLocation();
  const { features } = useAllFeatureAccess();

  const isActive = (path) => {
    if (path === '/') {
      return location.pathname === '/';
    }
    return location.pathname.startsWith(path);
  };

  const navItems = [
    {
      section: 'Main',
      items: [
        { path: '/', icon: LayoutDashboard, label: 'Dashboard' },
        { path: '/projects', icon: FolderOpen, label: 'Projects' },
        { path: '/assignments', icon: Briefcase, label: 'My Assignments' },
        { path: '/activity', icon: Activity, label: 'Activity Log' },
        { path: '/team', icon: Users, label: 'Team', featureKey: 'team' },
        { path: '/billing', icon: CreditCard, label: 'Billing' },
        { path: '/usage', icon: BarChart2, label: 'Usage' },
        { path: '/referral', icon: Gift, label: 'Referrals', badge: 'Earn' },
      ]
    },
    {
      section: 'Translation Tools',
      items: [
        { path: '/cat', icon: FileText, label: 'CAT Tool', badge: 'PRO', featureKey: 'cat' },
        { path: '/translation', icon: Languages, label: 'Basic Translation' },
        { path: '/mtpe', icon: PenTool, label: 'MTPE', featureKey: 'mtpe' },
        { path: '/mtpe-pro', icon: Target, label: 'MTPE Pro', badge: 'PRO', featureKey: 'mtpe_advanced' },
      ]
    },
    {
      section: 'Resources',
      items: [
        { path: '/tm', icon: Database, label: 'Translation Memory' },
        { path: '/glossary', icon: BookOpen, label: 'Glossary' },
      ]
    },
    {
      section: 'Quality',
      items: [
        { path: '/tqa', icon: ClipboardCheck, label: 'TQA', badge: 'NEW', featureKey: 'tqa' },
        { path: '/review', icon: FileSearch, label: 'Review & Proofread', badge: 'NEW', featureKey: 'review' },
      ]
    },
  ];

  const sidebarContent = (
    <>
      {navItems.map((section, idx) => (
        <div key={idx} className="apple-sidebar-section">
          {!collapsed && (
            <div className="apple-sidebar-section-title">{section.section}</div>
          )}
          <nav className="apple-sidebar-nav">
            {section.items.map((item) => {
              const isLocked = item.featureKey && features[item.featureKey]?.enabled === false;
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`apple-sidebar-link ${isActive(item.path) ? 'active' : ''} ${isLocked ? 'opacity-50' : ''}`}
                  data-testid={`sidebar-link-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
                  title={collapsed ? item.label : undefined}
                  onClick={onCloseMobile}
                >
                  <item.icon size={18} className="icon" />
                  {!collapsed && (
                    <>
                      <span>{item.label}</span>
                      {isLocked ? (
                        <Lock size={12} className="ml-auto opacity-60" />
                      ) : item.badge ? (
                        <span className={`apple-sidebar-badge ${(item.badge === 'NEW' || item.badge === 'Earn') ? 'success' : ''}`}>
                          {item.badge}
                        </span>
                      ) : null}
                    </>
                  )}
                </Link>
              );
            })}
          </nav>
        </div>
      ))}

      {/* Bottom Section */}
      <div className="apple-sidebar-section mt-auto">
        <nav className="apple-sidebar-nav">
          <Link
            to="/settings"
            className={`apple-sidebar-link ${isActive('/settings') ? 'active' : ''}`}
            data-testid="sidebar-link-settings"
            title={collapsed ? 'Settings' : undefined}
            onClick={onCloseMobile}
          >
            <Settings size={18} className="icon" />
            {!collapsed && <span>Settings</span>}
          </Link>
          <a
            href="mailto:support@t09n.com"
            className="apple-sidebar-link"
            data-testid="sidebar-link-help"
            title={collapsed ? 'Help & Support' : undefined}
          >
            <HelpCircle size={18} className="icon" />
            {!collapsed && <span>Help & Support</span>}
          </a>
        </nav>
      </div>

      {/* Upgrade Banner */}
      {!collapsed && (
        <div className={`mx-3 mt-4 p-4 rounded-xl border ${isDarkMode ? 'bg-[#1e2a3a] border-[#2a3f5f]' : 'bg-gradient-to-br from-blue-50 to-indigo-50 border-blue-100'}`}>
          <div className="flex items-center gap-2 mb-2">
            <Sparkles size={16} className={isDarkMode ? 'text-blue-400' : 'text-blue-600'} />
            <span className={`text-sm font-semibold ${isDarkMode ? 'text-blue-300' : 'text-blue-900'}`}>Pro Features</span>
          </div>
          <p className={`text-xs mb-3 leading-relaxed ${isDarkMode ? 'text-blue-400' : 'text-blue-700'}`}>
            Unlock advanced CAT tools, unlimited TM, and team collaboration.
          </p>
          <Link
            to="/pricing"
            className="apple-btn apple-btn-primary apple-btn-sm apple-btn-full"
            data-testid="sidebar-upgrade-btn"
          >
            Upgrade Now
          </Link>
        </div>
      )}
    </>
  );

  return (
    <>
      {/* Desktop Sidebar */}
      <aside 
        className={`apple-sidebar ${collapsed ? 'collapsed' : ''} hidden lg:block`}
        data-testid="apple-sidebar"
      >
        {sidebarContent}
      </aside>

      {/* Mobile Sidebar Overlay */}
      {isMobileOpen && (
        <div 
          className="fixed inset-0 bg-black/40 z-[899] lg:hidden"
          onClick={onCloseMobile}
        />
      )}

      {/* Mobile Sidebar */}
      <aside 
        className={`
          fixed top-0 left-0 bottom-0 w-[280px] bg-[#1a1b1e] dark:bg-[#1a1b1e] z-[900] 
          transform transition-transform duration-300 lg:hidden
          ${isMobileOpen ? 'translate-x-0' : '-translate-x-full'}
        `}
        data-testid="apple-sidebar-mobile"
      >
        <div className={`flex items-center justify-between p-4 border-b ${isDarkMode ? 'border-gray-700' : 'border-gray-200'}`}>
          <span className={`font-semibold ${isDarkMode ? 'text-gray-100' : 'text-gray-900'}`}>Menu</span>
          <button 
            onClick={onCloseMobile}
            className="apple-btn apple-btn-ghost apple-btn-icon"
          >
            <X size={20} />
          </button>
        </div>
        <div className="p-4 overflow-y-auto" style={{ height: 'calc(100% - 65px)' }}>
          {sidebarContent}
        </div>
      </aside>
    </>
  );
};

export default SidebarApple;
